import React, { useCallback, useEffect, useState } from "react";
import {
  IndianRupee,
  Trash,
  UploadCloud,
  Edit,
  Copy,
  Check,
} from "lucide-react";
import { db, storage, auth } from "../config/firebaseConfig";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

const paymentsRef = collection(db, "paymentsDetails");

const PaymentCard = () => {
  const [qrFile, setQrFile] = useState(null);
  const [upiid, setupiid] = useState("");
  const [receiverName, setReceiverName] = useState("");
  const [payments, setPayments] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const [uploadProgress, setUploadProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchPayments = useCallback(async () => {
    const snapshot = await getDocs(paymentsRef);
    setPayments(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
  }, []);

  // ✅ Fetch Payments
  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          const userRef = doc(db, "users", currentUser.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            const roles = userData.roles || [];
            const role = userData.role || "";

            if (
              role.toLowerCase() === "admin" ||
              (Array.isArray(roles) && roles.includes("admin"))
            ) {
              setIsAdmin(true);
            } else {
              setIsAdmin(false);
            }
          }
        } catch (err) {
          console.error("Error checking admin:", err);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // ✅ Upload QR File
  const handleUploadFile = (e) => {
    if (e.target.files[0]) {
      setQrFile(e.target.files[0]);
    }
  };

  // ✅ Save or Update
  const handleSave = async () => {
    if (!upiid || !receiverName) {
      alert("Please enter UPI ID & Receiver Name!");
      return;
    }

    setSaving(true);
    setStatus("Starting...");
    console.log("Starting save process");

    let upiqrimg = null;

    try {
      // 🔥 Upload to Firebase Storage
      if (qrFile && typeof qrFile !== "string") {
        console.log("Uploading file:", qrFile.name);
        setStatus("Uploading QR image...");
        const fileRef = ref(storage, `payment/${Date.now()}_${qrFile.name}`);
        const uploadTask = uploadBytesResumable(fileRef, qrFile);

        upiqrimg = await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
              console.log("Upload progress:", progress);
            },
            (error) => {
              console.error("Upload error:", error);
              reject(error);
            },
            async () => {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              console.log("Download URL:", downloadURL);
              resolve(downloadURL);
            },
          );
        });
        setStatus("Upload complete.");
      } else if (typeof qrFile === "string") {
        upiqrimg = qrFile;
        console.log("Using existing QR URL");
      }

      // 🔥 Save to Firestore
      setStatus(editingId ? "Updating details..." : "Saving...");
      console.log("Saving to Firestore, editingId:", editingId);
      if (editingId) {
        const docRef = doc(db, "paymentsDetails", editingId);
        await updateDoc(docRef, { upiid, receiverName, upiqrimg });
        console.log("Updated document:", editingId);
        setEditingId(null);
        setStatus("Updated successfully!");
      } else {
        const docRef = await addDoc(paymentsRef, {
          upiid,
          receiverName,
          upiqrimg,
        });
        console.log("Added new document:", docRef.id);
        setStatus("Saved successfully!");
      }
    } catch (error) {
      console.error("Save error:", error);
      setStatus("Error occurred!");
    }

    setupiid("");
    setReceiverName("");
    setQrFile(null);
    setUploadProgress(0);
    setSaving(false);
    console.log("Save process complete");

    fetchPayments();
  };

  // ✅ Delete
  const handleDelete = async (id, upiqrimg) => {
    if (window.confirm("Delete this payment method?")) {
      setStatus("Deleting...");
      const docRef = doc(db, "paymentsDetails", id);
      await deleteDoc(docRef);

      if (upiqrimg) {
        const fileRef = ref(storage, upiqrimg);
        await deleteObject(fileRef).catch(() => {});
      }
      fetchPayments();
      setStatus("Deleted successfully!");
    }
  };

  // ✅ Edit
  const handleEdit = (payment) => {
    setEditingId(payment.id);
    setupiid(payment.upiid);
    setReceiverName(payment.receiverName);
    setQrFile(payment.upiqrimg || null);
  };

  // ✅ Copy UPI
  const handleCopy = async (upiid) => {
    await navigator.clipboard.writeText(upiid);
    setCopiedId(upiid);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <p className="text-gray-500 animate-pulse">Checking access...</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      {/* Admin Section */}
      {isAdmin && (
        <div className="bg-white rounded-xl shadow-md p-6 space-y-4">
          <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl h-40 cursor-pointer hover:border-purple-500 transition relative">
            {qrFile ? (
              <img
                src={
                  typeof qrFile === "string"
                    ? qrFile
                    : URL.createObjectURL(qrFile)
                }
                alt="QR Preview"
                className="w-full h-full object-contain rounded-xl p-2"
              />
            ) : (
              <>
                <UploadCloud className="w-10 h-10 text-gray-400" />
                <span className="mt-2 text-gray-500 text-sm">
                  Upload QR Image*
                </span>
              </>
            )}
            <input
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleUploadFile}
            />
          </label>

          {uploadProgress > 0 && (
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-purple-600 h-2 rounded-full"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          )}
          {status && <p className="text-sm text-gray-600">{status}</p>}

          <input
            type="text"
            placeholder="UPI Id"
            className="border border-gray-300 p-3 rounded-xl w-full focus:outline-none focus:ring-2 focus:ring-purple-500"
            value={upiid}
            onChange={(e) => setupiid(e.target.value)}
            disabled={saving}
          />
          <input
            type="text"
            placeholder="Receiver Name"
            className="border border-gray-300 p-3 rounded-xl w-full focus:outline-none focus:ring-2 focus:ring-purple-500"
            value={receiverName}
            onChange={(e) => setReceiverName(e.target.value)}
            disabled={saving}
          />

          <button
            onClick={handleSave}
            disabled={saving}
            className={`${
              saving ? "bg-gray-400" : "bg-purple-700 hover:bg-purple-800"
            } text-white py-3 rounded-2xl w-full shadow-md flex items-center justify-center space-x-2 transition`}
          >
            <IndianRupee className="w-5 h-5" />
            <span>
              {saving
                ? "Saving..."
                : editingId
                  ? "Update Payment"
                  : "Save Payment"}
            </span>
          </button>
        </div>
      )}

      {/* Saved Payments */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">💳 Saved Payment Methods</h3>
        {payments.length === 0 ? (
          <p className="text-gray-500">No payment details found.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {payments.map((payment) => (
              <div
                key={payment.id}
                className="bg-white rounded-xl shadow-md p-4 flex flex-col space-y-3"
              >
                {payment.upiqrimg && (
                  <img
                    src={payment.upiqrimg}
                    alt="QR"
                    className="w-24 h-24 object-contain rounded-lg border self-center"
                  />
                )}
                <div>
                  <h4 className="font-semibold text-gray-800">
                    {payment.receiverName}
                  </h4>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <span className="truncate">{payment.upiid}</span>
                    <button
                      onClick={() => handleCopy(payment.upiid)}
                      className="p-1 rounded-full hover:bg-gray-200 transition"
                    >
                      {copiedId === payment.upiid ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {isAdmin && (
                  <div className="space-x-2 flex">
                    <button
                      onClick={() => handleEdit(payment)}
                      className="bg-yellow-400 text-black px-2 py-1 rounded-lg flex items-center space-x-1 hover:bg-yellow-500 transition"
                    >
                      <Edit className="w-4 h-4" />
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={() => handleDelete(payment.id, payment.upiqrimg)}
                      className="bg-red-500 text-white px-2 py-1 rounded-lg flex items-center space-x-1 hover:bg-red-600 transition"
                    >
                      <Trash className="w-4 h-4" />
                      <span>Delete</span>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentCard;


